import type { LucideIcon } from "lucide-react";
import {
	BadgeDollarSignIcon,
	BotIcon,
	FileTextIcon,
	FlaskConicalIcon,
	HandshakeIcon,
	HouseIcon,
	InboxIcon,
	MapIcon,
	MessageCircleIcon,
	PackageIcon,
	PhoneIcon,
	PlugIcon,
	ReceiptTextIcon,
	RefreshCwIcon,
	ScanSearchIcon,
	ScrollTextIcon,
	SettingsIcon,
	ShieldIcon,
	SlidersHorizontalIcon,
	UsersRoundIcon,
	WebhookIcon,
} from "lucide-react";

export interface NavEntry {
	id: string;
	label: string;
	icon: LucideIcon;
	/** Path under /app/$organizationSlug; "" is the org home. */
	segment: string;
	/** One line for the command menu. */
	hint: string;
	keywords?: string[];
	/**
	 * Shown in the sidebar only when this is true for the retailer: Calls appears once calling
	 * is on for a number or a call exists. The command menu always lists it.
	 */
	when?: "calls";
}

export interface NavGroup {
	label?: string;
	entries: readonly NavEntry[];
}

/**
 * Single source for the sidebar, the header breadcrumb and the command menu.
 * Every entry is scoped to the active retailer; Platform is added separately
 * for Abiroot staff.
 */
export const NAV_GROUPS: readonly NavGroup[] = [
	{
		entries: [
			{
				id: "overview",
				label: "Overview",
				icon: HouseIcon,
				segment: "",
				hint: "How the agent is doing",
				keywords: ["home", "dashboard", "insights", "stats"],
			},
			{
				id: "conversations",
				label: "Inbox",
				icon: InboxIcon,
				segment: "conversations",
				hint: "Every shopper conversation",
				keywords: ["conversations", "chats", "messages", "escalated"],
			},
			{
				id: "orders",
				label: "Orders",
				icon: ReceiptTextIcon,
				segment: "orders",
				hint: "New orders to accept, and every order's progress",
				keywords: [
					"orders",
					"queue",
					"accept",
					"delivery",
					"pickup",
					"tickets",
					"checkout",
				],
			},
			{
				id: "calls",
				label: "Calls",
				icon: PhoneIcon,
				segment: "calls",
				hint: "WhatsApp calls, recordings and transcripts",
				keywords: [
					"calls",
					"voice",
					"phone",
					"recording",
					"missed",
					"wacid",
				],
				when: "calls",
			},
			{
				id: "customers",
				label: "Customers",
				icon: UsersRoundIcon,
				segment: "customers",
				hint: "Shoppers, their addresses, orders and consents",
				keywords: [
					"customers",
					"shoppers",
					"addresses",
					"marketing",
					"opt in",
					"privacy",
					"export",
				],
			},
			{
				id: "conversions",
				label: "Conversions",
				icon: BadgeDollarSignIcon,
				segment: "conversions",
				hint: "Orders the agent brought and what it earned",
				keywords: ["orders", "revenue", "roi", "sales", "attribution"],
			},
		],
	},
	{
		label: "Agent",
		entries: [
			{
				id: "agent",
				label: "Configure",
				icon: BotIcon,
				segment: "agent",
				hint: "The assistant, how the chat looks, voice, tools and models",
				keywords: [
					"agent",
					"prompt",
					"persona",
					"publish",
					"playground",
					"brand",
					"colours",
					"theme",
					"dark",
					"voice",
					"welcome",
					"tools",
					"handoff",
					"models",
				],
			},
			{
				id: "evals",
				label: "Evals",
				icon: FlaskConicalIcon,
				segment: "evals",
				hint: "Benchmark the agent before publishing",
				keywords: ["tests", "bench", "benchmark", "quality"],
			},
		],
	},
	{
		label: "Catalog",
		entries: [
			{
				id: "catalog",
				label: "Products",
				icon: PackageIcon,
				segment: "catalog",
				hint: "Products, prices, stock and search words",
				keywords: ["catalog", "items", "barcode", "stock"],
			},
			{
				id: "sources",
				label: "Sources and sync",
				icon: RefreshCwIcon,
				segment: "sources",
				hint: "Where products come from and every refresh",
				keywords: [
					"sync",
					"refresh",
					"connector",
					"schedule",
					"feed",
					"shopify",
					"woocommerce",
					"salla",
					"zid",
					"suppy",
					"import",
				],
			},
			{
				id: "branches",
				label: "Branches",
				icon: MapIcon,
				segment: "branches",
				hint: "Branches, delivery areas, hours and pickup",
				keywords: [
					"stores",
					"branches",
					"delivery zones",
					"areas",
					"map",
					"hours",
					"pickup",
					"radius",
				],
			},
			{
				id: "search",
				label: "Search tester",
				icon: ScanSearchIcon,
				segment: "search",
				hint: "Run the agent's product search",
				keywords: ["search", "explain", "ranking"],
			},
		],
	},
	{
		label: "Connect",
		entries: [
			{
				id: "install",
				label: "Install",
				icon: PlugIcon,
				segment: "install",
				hint: "Put the chat in your app or site",
				keywords: [
					"embed",
					"launch keys",
					"launch token",
					"allowed origins",
					"widget",
					"app",
					"api",
					"scopes",
				],
			},
			{
				id: "channels",
				label: "Channels",
				icon: MessageCircleIcon,
				segment: "channels",
				hint: "Your WhatsApp numbers, their health and settings",
				keywords: [
					"whatsapp",
					"messaging",
					"phone number",
					"meta",
					"templates",
					"catalog",
					"calls",
					"profile",
					"quality",
					"ice breakers",
					"entry links",
					"qr code",
					"embedded signup",
				],
			},
			{
				id: "integrations",
				label: "Integrations",
				icon: WebhookIcon,
				segment: "integrations",
				hint: "Webhooks to your systems and their delivery log",
				keywords: [
					"webhooks",
					"endpoints",
					"events",
					"deliveries",
					"signing secret",
				],
			},
			{
				id: "logs",
				label: "Logs",
				icon: ScrollTextIcon,
				segment: "logs",
				hint: "Every exchange with Meta and your order system",
				keywords: [
					"logs",
					"webhook log",
					"errors",
					"debugger",
					"flow endpoint",
					"graph",
					"override",
					"tunnel",
					"curl",
					"wamid",
				],
			},
		],
	},
];

/**
 * Pages inside a section that people jump to by name: listed in the command menu only, the
 * sidebar keeps the section.
 */
export const NAV_SHORTCUTS: readonly NavEntry[] = [
	{
		id: "templates",
		label: "WhatsApp templates",
		icon: FileTextIcon,
		segment: "channels/templates",
		hint: "Order updates and other messages Meta approves",
		keywords: ["templates", "order updates", "approval", "utility"],
	},
	{
		id: "orders-setup",
		label: "Ordering setup",
		icon: SlidersHorizontalIcon,
		segment: "orders/setup",
		hint: "How orders reach you, delivery, payment and messages",
		keywords: ["ordering", "order system", "orders api", "delivery fee"],
	},
];

export const SETTINGS_ENTRY: NavEntry = {
	id: "settings",
	label: "Settings",
	icon: SettingsIcon,
	segment: "settings",
	hint: "Team, API keys and audit log",
	keywords: ["members", "team", "api keys", "roles", "audit"],
};

export const PLATFORM_ICON = ShieldIcon;

export const PARTNER_ICON = HandshakeIcon;

export function orgHref(slug: string, segment: string) {
	return segment ? `/app/${slug}/${segment}` : `/app/${slug}`;
}
